<script setup>
import { ref, onMounted } from 'vue'
import { router } from '@inertiajs/vue3'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import SettingsBankAccountForm from '@/Components/Forms/SettingsBankAccountForm.vue'
import StripeConnectForm from '@/Components/Teacher/StripeConnectForm.vue'
import { ExternalLink } from 'lucide-vue-next'

const props = defineProps({
  bankAccount: {
    type: Object,
    default: null
  },
  connectStatus: {
    type: String,
    default: 'none'
  },
  connectAccountId: {
    type: String,
    default: null
  },
  expressLoginUrl: {
    type: String,
    default: null
  },
  connectError: {
    type: String,
    default: null
  },
  stripeKey: {
    type: String,
    required: true
  }
})

const errors = ref({})
const isOnboarding = ref(false)

const bankDetails = ref({
  account_holder_name: '',
  account_number: '',
  sort_code: '',
  bank_name: '',
  address_line_1: '',
  address_line_2: '',
  city: '',
  county: '',
  postcode: '',
})

// Format sort code helper
const formatSortCode = (value) => {
  if (!value) return ''

  // Remove any non-digit characters
  const digits = value.replace(/[^0-9]/g, '')

  // Format as XX-XX-XX
  if (digits.length >= 6) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`
  } else if (digits.length >= 4) {
    return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`
  } else if (digits.length >= 2) {
    return `${digits.slice(0, 2)}-${digits.slice(2)}`
  }

  return digits
}

const connectToStripe = () => {
  isOnboarding.value = true
}

// Note: Stripe tells the page nothing about what was filled in, so the status is read again
const finishOnboarding = () => {
  isOnboarding.value = false
  router.reload()
}

onMounted(() => {
  if (props.bankAccount) {
    bankDetails.value = {
      account_holder_name: props.bankAccount.account_holder_name || '',
      account_number: props.bankAccount.account_number || '',
      sort_code: formatSortCode(props.bankAccount.sort_code || ''),
      bank_name: props.bankAccount.bank?.slug || '',
      address_line_1: props.bankAccount.address1 || '',
      address_line_2: props.bankAccount.address2 || '',
      city: props.bankAccount.city || '',
      county: props.bankAccount.county || '',
      postcode: props.bankAccount.postcode || '',
    }
  }
})
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Stripe Connect Status -->
      <div class="rounded-lg border bg-white border-gray-100 p-6">
        <h3 class="text-lg font-semibold text-gray-900">Stripe Payouts</h3>

        <StripeConnectForm
          v-if="connectStatus === 'active' || connectStatus === 'restricted'"
          component="notification-banner"
          :stripe-key="stripeKey"
          class="mt-3"
        />

        <StripeConnectForm
          v-if="isOnboarding"
          component="account-onboarding"
          :stripe-key="stripeKey"
          class="mt-3"
          @exit="finishOnboarding"
        />

        <!-- Active -->
        <div v-else-if="connectStatus === 'active'" class="mt-3">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
              Payouts Active
            </span>
            <a
              v-if="expressLoginUrl"
              :href="expressLoginUrl"
              target="_blank"
              class="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors"
            >
              Go to Connect
              <ExternalLink class="w-3.5 h-3.5" />
            </a>
          </div>
          <p class="mt-2 text-sm text-gray-600">
            Your Stripe account is verified and payouts are enabled. Earnings will be deposited to your bank account automatically.
          </p>
        </div>

        <!-- Restricted (onboarded but payouts not enabled) -->
        <div v-else-if="connectStatus === 'restricted'" class="mt-3">
          <div class="flex items-center gap-2">
            <span class="inline-flex items-center rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700">
              Payouts Restricted
            </span>
            <a
              v-if="expressLoginUrl"
              :href="expressLoginUrl"
              target="_blank"
              class="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors"
            >
              Go to Stripe Dashboard
              <ExternalLink class="w-3.5 h-3.5" />
            </a>
          </div>
          <p class="mt-2 text-sm text-gray-600">
            Your Stripe account is set up but payouts are not yet enabled. Stripe may require additional verification before you can receive payments. Please check your Stripe dashboard for any outstanding requirements.
          </p>
          <button
            @click="connectToStripe"
            class="mt-3 inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
          >
            Complete Verification
          </button>
        </div>

        <!-- Pending Verification -->
        <div v-else-if="connectStatus === 'pending_verification'" class="mt-3">
          <p class="text-sm text-gray-600 mb-3">
            Your Stripe account has been created but needs identity verification before you can receive payouts.
          </p>
          <button
            @click="connectToStripe"
            class="inline-flex items-center rounded-md bg-amber-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-amber-500"
          >
            Verify Identity
          </button>
        </div>

        <!-- Not Connected -->
        <div v-else class="mt-3">
          <p class="text-sm text-gray-600 mb-3">
            Connect your account to Stripe to receive automatic payouts to your bank account.
          </p>
          <button
            @click="connectToStripe"
            class="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500"
          >
            Connect to Stripe
          </button>
        </div>

        <!-- Error Messages -->
        <div v-if="connectError" class="mt-4 space-y-2">
          <div class="rounded-lg bg-red-50 border border-red-200 p-3">
            <p class="text-sm font-medium text-red-800">Latest Payout Issue</p>
            <p class="text-sm text-red-700 mt-1">{{ connectError }}</p>
          </div>
        </div>
      </div>

      <!-- Bank Account Form (Read-only) -->
      <SettingsBankAccountForm
        :model-value="bankDetails"
        :errors="errors"
        title="Payment Information"
        description="Your bank details are shown below. Please contact support if you need to make changes."
        :readonly="true"
      />
    </div>
  </SettingsLayout>
</template>
